import React, { useState, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { IconButton } from '@startuptools/ui/IconButton';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import { Box } from '@startuptools/ui/Box';
import { Typography } from '@startuptools/ui/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { isNil, toNumber } from 'lodash-es';
import { CommentCell } from '@startuptools/ui/CommentCell';
import { BigNumber } from 'bignumber.js';
import { rangeCount } from '@startuptools/common/transactions';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { ShareTransactionGroupsDocument, type GqlShareTransactionGroupsQuery } from '../Transactions.graphql';
import { type ArrayElement } from '@startuptools/common/common';
import { useDocGenTransactionNoteMutation, useStSetCommentMutation } from '../../../../graphql/react-operations';
import { PDFButton } from '@startuptools/business-ui/document/PDFPreviewButton';
import { apiClient } from '../../../../common/http';
import { Row } from './transaction-table/components/Row';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { GqlCurrency, GqlTransactionKind } from '../../../../graphql/base-types.graphql';
import { DeleteTransactions } from './transaction-table/components/DeleteTransactions';
import { CommentDialog } from '../../../common/components/CommentDialog';
import { EditPriceDialog } from './transaction-table/components/EditPriceDialog';
import { TransactionsViewDocument } from '../transactions-view.graphql';

export const TransactionsTable = ({
  transactionGroup,
  navigateToStg,
  showNewTransactionButton,
  forbiddenToDeleteStg,
  showShareRange,
}: {
  transactionGroup: ArrayElement<GqlShareTransactionGroupsQuery['shareTransactionGroups']>;
  navigateToStg?: () => void;
  showNewTransactionButton: boolean;
  forbiddenToDeleteStg: boolean;
  showShareRange: boolean;
}) => {
  const companyId = useCompanyId();
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [openedComment, setOpenedComment] = useState<{ comment?: string | null; id: string } | null>(null);
  const [openedPrice, setOpenedPrice] = useState<{
    pricePerShare?: string | null;
    currency: GqlCurrency;
    id: string;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<false | 'group' | 'transactions'>(false);
  // eslint-disable-next-line sut/loading-mutation
  const [docGenTransactionNote] = useDocGenTransactionNoteMutation();

  const [doUpdate, { loading }] = useStSetCommentMutation({
    refetchQueries: [ShareTransactionGroupsDocument, TransactionsViewDocument],
  });

  const { i18n } = useLingui();

  const { shareTransactions } = transactionGroup;

  // const unformattedNumberOfShares = useMemo(() => {
  //   if (isNil(shareTransactions)) {
  //     return null;
  //   }
  //
  //   return shareTransactions.reduce(
  //     (total, transaction) => total + rangeCount(Number(transaction.blockStart), Number(transaction.blockEnd)),
  //     0,
  //   );
  // }, [shareTransactions]);

  const sums = useMemo(() => {
    if (isNil(shareTransactions)) {
      return null;
    }
    return shareTransactions.reduce(
      (total, transaction) => {
        if (isNil(transaction.pricePerShare)) {
          return total;
        }
        if (!(transaction.currency in total)) {
          total[transaction.currency] = { totalPaid: 0, totalNumberOfShares: 0 };
        }
        const numberOfShares = rangeCount(Number(transaction.blockStart), Number(transaction.blockEnd));
        total[transaction.currency]!.totalNumberOfShares += numberOfShares;
        const totalPaid = new BigNumber(numberOfShares)
          .times(transaction.pricePerShare)
          .plus(total[transaction.currency]!.totalPaid)
          .toNumber();
        total[transaction.currency]!.totalPaid = totalPaid;
        return total;
      },
      {} as { [k in GqlCurrency]?: { totalNumberOfShares: number; totalPaid: number } },
    );
  }, [shareTransactions]);

  if (isNil(shareTransactions)) {
    return null;
  }

  const handleSelectionChanged = (changedId: string) => {
    if (selectedTransactions.includes(changedId)) {
      setSelectedTransactions(selectedTransactions.filter(id => id !== changedId));
    } else {
      setSelectedTransactions([...selectedTransactions, changedId]);
    }
  };

  const handleSelectChangeAll = () => {
    if (selectedTransactions.length === shareTransactions.length) {
      setSelectedTransactions([]);
    } else if (selectedTransactions.length >= 0) {
      setSelectedTransactions(shareTransactions.map(st => st.id));
    }
  };

  return (
    <Stack gap={1} padding={1}>
      <Stack direction="row" gap={2}>
        {!(
          transactionGroup.kind === GqlTransactionKind.Merge ||
          transactionGroup.kind === GqlTransactionKind.Split ||
          transactionGroup.kind === GqlTransactionKind.CurrencyChange
        ) &&
          !!showNewTransactionButton &&
          !isNil(navigateToStg) && (
            <Button type="button" onClick={navigateToStg}>
              <Trans id="Edit" />
            </Button>
          )}

        <Button
          variant="outlined"
          onClick={() => {
            if (
              transactionGroup.kind === GqlTransactionKind.Merge ||
              transactionGroup.kind === GqlTransactionKind.Split
            ) {
              setConfirmDelete('group');
            } else {
              setConfirmDelete('transactions');
            }
          }}
          disabled={
            !(
              transactionGroup?.kind === GqlTransactionKind.Merge ||
              transactionGroup?.kind === GqlTransactionKind.Split ||
              (shareTransactions && shareTransactions.length === 0) ||
              (selectedTransactions && selectedTransactions.length > 0)
            )
          }
        >
          <Trans id="Remove" />
        </Button>
      </Stack>

      <DeleteTransactions
        transactionGroup={transactionGroup}
        shareTransactions={shareTransactions}
        forbiddenToDeleteStg={forbiddenToDeleteStg}
        selectedTransactions={selectedTransactions}
        setSelectedTransactions={setSelectedTransactions}
        onClose={() => setConfirmDelete(false)}
        open={confirmDelete}
      />

      {openedComment && (
        <CommentDialog
          onClose={() => setOpenedComment(null)}
          comment={openedComment?.comment}
          onSave={async comment => {
            await doUpdate({
              variables: { companyId, input: { comment, id: openedComment?.id } },
            });
          }}
          loading={loading}
        />
      )}

      {openedPrice && (
        <EditPriceDialog
          onClose={() => setOpenedPrice(null)}
          transaction={openedPrice}
          allowChangeCurrency={!transactionGroup.snrShareCapitalChangeId}
        />
      )}

      <Table sx={{ tableLayout: 'auto' }}>
        <thead>
          <Row
            kind={transactionGroup.kind}
            showShareRange={showShareRange}
            component="th"
            slots={{
              selection: (
                <Checkbox
                  checked={selectedTransactions.length > 0}
                  indeterminate={
                    selectedTransactions.length > 0 && selectedTransactions.length !== shareTransactions.length
                  }
                  onChange={handleSelectChangeAll}
                />
              ),
              buyer: [
                GqlTransactionKind.Split,
                GqlTransactionKind.ShareClassConvert,
                GqlTransactionKind.BlockPartition,
              ].includes(transactionGroup.kind) ? (
                <Trans id="Shareholder" />
              ) : (
                <Trans id="Buyer" />
              ),
              seller: [GqlTransactionKind.Merge].includes(transactionGroup.kind) ? (
                <Trans id="Shareholder" />
              ) : (
                <Trans id="Seller" />
              ),
              shareClass: <Trans id="Share Class" />,
              // totalShares: <Trans id="Total shares" />,
              newShares: [
                GqlTransactionKind.Transfer,
                GqlTransactionKind.ShareClassConvert,
                GqlTransactionKind.BlockPartition,
              ].includes(transactionGroup.kind) ? (
                <Trans id="Shares" />
              ) : [GqlTransactionKind.Merge, GqlTransactionKind.Unissue].includes(transactionGroup.kind) ? (
                <Trans id="Redeemed shares" />
              ) : (
                <Trans id="New shares" />
              ),
              shareRange: <Trans id="Share Range" />,
              pricePerShare: <Trans id="Price/share" />,
              paid: <Trans id="Paid" />,
              actions: null,
            }}
          />
        </thead>

        <tbody>
          {shareTransactions.map(transaction => {
            const unformattedNumberOfShares = rangeCount(
              toNumber(transaction.blockStart),
              toNumber(transaction.blockEnd),
            );
            const unformattedTotalPaid =
              transaction.pricePerShare &&
              new BigNumber(rangeCount(toNumber(transaction.blockStart), toNumber(transaction.blockEnd)))
                .times(transaction.pricePerShare)
                .toNumber();

            return (
              <React.Fragment key={transaction.id}>
                <Row
                  kind={transactionGroup.kind}
                  showShareRange={showShareRange}
                  slots={{
                    selection: (
                      <Checkbox
                        checked={selectedTransactions.includes(transaction.id)}
                        onChange={() => handleSelectionChanged(transaction.id)}
                      />
                    ),
                    buyer: transaction.buyer?.identity && (
                      <Identity identity={transaction.buyer.identity} showAvatar={shareTransactions.length <= 50} />
                    ),
                    seller: transaction.seller?.identity && (
                      <Identity identity={transaction.seller.identity} showAvatar={shareTransactions.length <= 50} />
                    ),
                    shareClass:
                      transactionGroup.kind === GqlTransactionKind.ShareClassConvert ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {transaction.shareClassFrom?.name}
                          <ArrowForwardIcon />
                          {transaction.shareClassTo?.name}
                        </Box>
                      ) : (
                        (transaction.shareClassTo?.name ?? transaction.shareClassFrom?.name)
                      ),
                    // totalShares: lastTransaction && (
                    //   <TotalShares
                    //     ownerId={transaction[showTotalSharesOfSeller ? 'sellerId' : 'buyerId']!}
                    //     transactionId={lastTransaction.id}
                    //   />
                    // ),
                    newShares: (
                      <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                        {unformattedNumberOfShares}
                      </NumberFormat>
                    ),
                    shareRange: (
                      <>
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                          {transaction.blockStart}
                        </NumberFormat>
                        {'-'}
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                          {transaction.blockEnd}
                        </NumberFormat>
                      </>
                    ),

                    pricePerShare: transaction.pricePerShare && (
                      <NumberFormat
                        locale={i18n.locale}
                        variant="currency"
                        currency={transaction.currency}
                        minimumFractionDigits={0}
                        maximumFractionDigits={5}
                      >
                        {transaction.pricePerShare}
                      </NumberFormat>
                    ),

                    paid: unformattedTotalPaid && (
                      <NumberFormat
                        locale={i18n.locale}
                        variant="currency"
                        currency={transaction.currency}
                        minimumFractionDigits={0}
                        maximumFractionDigits={5}
                      >
                        {unformattedTotalPaid}
                      </NumberFormat>
                    ),
                    actions: (
                      <Stack direction="row" gap={1} justifyContent="end">
                        {[GqlTransactionKind.Transfer, GqlTransactionKind.Issue, GqlTransactionKind.Unissue].includes(
                          transactionGroup.kind,
                        ) && (
                          <>
                            <PDFButton
                              label={i18n.t('View PDF')}
                              button={({ label, ...props }) => (
                                <Tooltip title={label}>
                                  <IconButton color="primary" name={label} {...props} variant="outlined">
                                    <ReceiptLongIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              generatePdf={async language => {
                                if (!language) throw new Error(' language is undefined');
                                const { data } = await docGenTransactionNote({
                                  variables: {
                                    companyId,
                                    shareTransactionId: transaction.id,
                                    docLanguage: {
                                      primary_lang: language.primaryLang,
                                      secondary_lang: language.secondaryLang,
                                    },
                                  },
                                });
                                if (!data) {
                                  throw new Error('no data');
                                }
                                const { filename } = data.docGenTransactionNote.docGen.docGenDetails;
                                if (!filename) {
                                  throw new Error('no filename');
                                }
                                const blob = await apiClient.preview2({
                                  filename,
                                  docGens: [data.docGenTransactionNote.docGen],
                                });
                                return { filename, blob };
                              }}
                            />
                            <Tooltip title={<Trans id="Edit Price per Share" />}>
                              <IconButton
                                onClick={() =>
                                  setOpenedPrice({
                                    pricePerShare: transaction.pricePerShare ?? '',
                                    currency: transaction.currency,
                                    id: transaction.id ?? '',
                                  })
                                }
                                color="primary"
                                variant="outlined"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title={<Trans id="Edit Comment" />}>
                          <IconButton onClick={() => setOpenedComment(transaction)} color="primary" variant="outlined">
                            <CommentIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ),
                  }}
                  component="td"
                />
                {transaction.comment && (
                  <tr>
                    <td valign="top">
                      <Box sx={{ paddingY: '13px' }}>
                        <Typography fontWeight={600}>
                          <Trans id="Comment:" />
                        </Typography>
                      </Box>
                    </td>
                    <CommentCell colSpan={5} comment={transaction.comment} />
                    <td></td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          {sums &&
            Object.entries(sums).map(([currency, sum]) => (
              <Row
                key={currency}
                kind={transactionGroup.kind}
                showShareRange={showShareRange}
                component="th"
                slots={{
                  selection: null,
                  buyer: null,
                  seller: null,
                  shareClass: null,
                  pricePerShare: null,
                  // totalShares: lastTransaction ? (
                  //   <TotalShares
                  //     transactionId={lastTransaction.id}
                  //     ownerIds={transactionGroup.shareTransactions
                  //       .filter(st => Boolean(st[showTotalSharesOfSeller ? 'sellerId' : 'buyerId']!))
                  //       .map(st => st[showTotalSharesOfSeller ? 'sellerId' : 'buyerId']!)}
                  //   />
                  // ) : null,
                  newShares: (
                    <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                      {sum.totalNumberOfShares}
                    </NumberFormat>
                  ),
                  shareRange: null,
                  paid: (
                    <NumberFormat locale={i18n.locale} variant="currency" currency={currency}>
                      {sum.totalPaid}
                    </NumberFormat>
                  ),
                  actions: null,
                }}
              />
            ))}
        </tfoot>
      </Table>
    </Stack>
  );
};
